import { CustomLoader } from "@admin/components/ui/custom-loader";
import { useEffect, useCallback, useState, useMemo } from "react";
import {
  DataTable,
  buildListMasterTableColumns,
} from "../../../components/data-table";
import { ListMasterPageLayout } from "../../../components/list-master";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import { Switch } from "../../../components/ui/switch";
import { toast } from "sonner";
import {
  donorCategoryMasterAPI,
  type DonorCategoryMaster,
  type DonorCategoryMasterCreatePayload,
} from "../../../services/api";
import { CommonPagination } from "../../../components/CommonPagination";

type DonorCategoryFormState = {
  name: string;
  is_active: boolean;
};

const initialFormState: DonorCategoryFormState = {
  name: "",
  is_active: true,
};

function getErrorMessage(
  err: unknown,
  fallback: string = "Failed to save donor category"
) {
  if (err && typeof err === "object" && "response" in err) {
    const response = (
      err as {
        response?: {
          data?: { message?: string; detail?: string; error?: string };
        };
      }
    ).response;
    return (
      response?.data?.message ??
      response?.data?.detail ??
      response?.data?.error ??
      (err as unknown as Error)?.message ??
      fallback
    );
  }
  return (err as Error)?.message ?? fallback;
}

type DonorCategoryPageProps = {
  embedded?: boolean;
};

export default function DonorCategoryPage({
  embedded = false,
}: DonorCategoryPageProps) {
  const [donorCategories, setDonorCategories] = useState<DonorCategoryMaster[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<DonorCategoryFormState>(initialFormState);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editing, setEditing] = useState<DonorCategoryMaster | null>(null);
  const [deleting, setDeleting] = useState<DonorCategoryMaster | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const resetForm = () => {
    setForm(initialFormState);
    setEditing(null);
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await donorCategoryMasterAPI.listPaginated({
        page: currentPage,
        page_size: pageSize,
        search: searchTerm.trim() || undefined,
      });
      const list = response.data ?? response.results ?? [];
      const count = response.count ?? response.total_count ?? list.length;
      const computedTotalPages =
        response.total_pages ?? Math.ceil(count / pageSize);

      setDonorCategories(Array.isArray(list) ? list : []);
      setTotalItems(count);
      setTotalPages(computedTotalPages);
    } catch {
      toast.error("Failed to load donor categories");
      setDonorCategories([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchList();
    }, 300);

    return () => clearTimeout(timer);
  }, [fetchList]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Title / Name is required");
      return;
    }

    setSubmitLoading(true);
    try {
      const payload: DonorCategoryMasterCreatePayload = {
        name: form.name.trim(),
        is_active: form.is_active,
      };

      if (editing) {
        await donorCategoryMasterAPI.update(editing.id, payload);
        toast.success("Donor type updated successfully");
      } else {
        await donorCategoryMasterAPI.create(payload);
        toast.success("Donor type created successfully");
      }

      setDialogOpen(false);
      resetForm();
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchList();
      }
    } catch (err: unknown) {
      toast.error(String(getErrorMessage(err)));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) {
      return;
    }

    setDeleteLoading(true);
    try {
      await donorCategoryMasterAPI.delete(deleting.id);
      toast.success("Donor type deleted successfully");
      setDeleting(null);
      if (donorCategories.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchList();
      }
    } catch (err: unknown) {
      toast.error(
        String(getErrorMessage(err, "Failed to delete donor category"))
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const openEdit = useCallback((donorCategory: DonorCategoryMaster) => {
    setEditing(donorCategory);
    setForm({
      name: donorCategory.name,
      is_active: donorCategory.is_active ?? true,
    });
    setDialogOpen(true);
  }, []);

  const tableColumns = useMemo(
    () =>
      buildListMasterTableColumns<DonorCategoryMaster>({
        currentPage,
        pageSize,
        onEdit: openEdit,
        onDelete: setDeleting,
        showCode: false,
        nameColumnLabel: "Title / Name",
      }),
    [currentPage, pageSize, openEdit]
  );

  return (
    <div className={embedded ? "" : "p-6"}>
      {!embedded && (
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground">Donor Category</h1>
          <p className="text-muted-foreground">
            Manage donor categories used in donation records
          </p>
        </div>
      )}
      <ListMasterPageLayout
        tableId="list-master-donor-category"
        tableColumns={tableColumns}
        listTitle={`Donor Categories (${totalItems})`}
        searchPlaceholder="Search donor categories..."
        searchTerm={searchTerm}
        onSearchChange={v => {
          setSearchTerm(v);
          setCurrentPage(1);
        }}
        headerActions={
          <Dialog
            open={dialogOpen}
            onOpenChange={open => {
              setDialogOpen(open);
              if (!open) {
                resetForm();
              }
            }}
          >
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  resetForm();
                  setDialogOpen(true);
                }}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Create new
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editing
                    ? "Edit Donor Category"
                    : "Create New Donor Category"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="donor-category-name">Title / Name *</Label>
                  <Input
                    id="donor-category-name"
                    value={form.name}
                    onChange={e =>
                      setForm(prev => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="Enter title or name"
                  />
                </div>
                <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
                  <div>
                    <Label htmlFor="donor-category-active">Status</Label>
                    <p className="text-xs text-muted-foreground">
                      {form.is_active ? "Active" : "Inactive"}
                    </p>
                  </div>
                  <Switch
                    id="donor-category-active"
                    checked={form.is_active}
                    onCheckedChange={checked =>
                      setForm(prev => ({ ...prev, is_active: checked }))
                    }
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    disabled={submitLoading}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={submitLoading}>
                    {submitLoading ? (
                      <CustomLoader size="xs" />
                    ) : editing ? (
                      "Update"
                    ) : (
                      "Create"
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        }
      >
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <CustomLoader size="md" />
          </div>
        ) : (
          <DataTable
            embedded
            hideToolbar
            tableId="list-master-donor-category"
            columns={tableColumns}
            data={donorCategories}
            getRowKey={d => d.id}
            emptyMessage={
              totalItems === 0 && !searchTerm.trim()
                ? "No donor categories yet. Create one to get started."
                : "No donor categories found matching your search."
            }
          />
        )}

        {totalItems > 0 && (
          <div className="mt-4 border-t pt-4">
            <CommonPagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalCount={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={size => {
                setPageSize(size);
                setCurrentPage(1);
              }}
            />
          </div>
        )}
      </ListMasterPageLayout>

      <Dialog
        open={!!deleting}
        onOpenChange={open => !open && setDeleting(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Donor Category</DialogTitle>
          </DialogHeader>
          {deleting && (
            <>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete <strong>{deleting.name}</strong>
                ?
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleting(null)}
                  disabled={deleteLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? <CustomLoader size="xs" /> : "Delete"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
